import * as React from "react";
import classnames from "classnames";

import HoverTooltip from "lib/HoverTooltip";
import { ExternalLink } from "lib/icons/ExternalLink";
import { blue500 } from "lib/themes/colors";
import Breadcrumbs from "./Breadcrumbs";
import ExpandableRecordList from "./ExpandableRecordList";
import { EnhancedBuyerData, RecordDetails } from "./landing_types";
import { capitalizeFirstLetter, SEO_URLS } from "./landing_utils";
import { Link, NewLinksSection } from "./LandingPageLinks";
import {
  Footer,
  FooterSignUpSection,
  Header,
  PromoSection,
  signupLink,
  usePageViewLogger,
} from "./shared";

import css from "./BuyerLandingPage.module.scss";

import DOWN_CHEVRON_ICON from "../../../assets/images/icons/down.svg";

type BuyerLandingPageConfig = {
  records: RecordDetails[];
  metadata: {
    logo_url: string | null;
    category: string[] | null;
    url: string | null;
    twitter_handle: string | null;
    employee_count: string | null;
    annual_spend: string | null;
    location_summary: string;
  } | null;
  top_suppliers: { name: string; id: number; sme: boolean; award_count: number }[];
  contacts: {
    position: string;
    department: string;
    job_title: string;
    categories: string[];
    seniority: number;
  }[];
  name: string;
  object_type: "tenders" | "upcoming_expiries" | "recent_awards";
};

type BuyerLinkSpamType = {
  similar_buyers: Link[];
  other_buyers: Link[];
  topics_of_interest_to_buyer: Link[];
  buyer_sources: Link[];
};

type Props = {
  config: BuyerLandingPageConfig;
  app_root_url: string;
  buyer_linkspam: BuyerLinkSpamType;
  current_page: number;
  total_results_count: number;
  extra_params_for_paging?: Record<string, string>;
  enhanced_buyer_data?: EnhancedBuyerData;
  buyer_guid: string;
};

// TODO: merge and optimise with existing function in normal buyer profile
type Seniority = "Chief Officer" | "C-level & Execs" | "Management";
function oscarToSeniorityMapping(level: number): Seniority {
  switch (level) {
    case 1:
      return "Chief Officer";
    case 2:
      return "C-level & Execs";
    case 3:
    case 4:
    case 5:
    default:
      return "Management";
  }
}

function ContactsRow({
  contacts,
  label,
  appRootUrl,
  buyer_guid,
}: {
  label: string;
  contacts: BuyerLandingPageConfig["contacts"];
  appRootUrl: string;
  buyer_guid: string;
}) {
  const [limit, setLimit] = React.useState(3);
  const invisibleContactCount = contacts.length - limit;

  return (
    <div>
      <h4 className={css.contactType}>{label}</h4>
      <ul className={css.contactsList}>
        {contacts.map((c, i) => (
          <HoverTooltip
            key={i}
            renderTrigger={(showTooltip, hideTooltip, setPosition) => (
              <li
                className={classnames({
                  [css.contactCard]: true,
                  [css.hidden]: i >= limit,
                })}
                onMouseOver={showTooltip}
                onMouseOut={hideTooltip}
                onMouseMove={setPosition}
              >
                <a href={signupLink(appRootUrl, "buyer", buyer_guid)} data-id="signup_cta">
                  <h4>{c.job_title}</h4>
                  <div className={css.blur}>Redacted name</div>
                  <div>{c.categories.join(", ")}</div>
                  <div>
                    <span className={css.blur}>redacted_email</span>@
                    <span className={css.blur}>subdomain.gov</span>.uk
                  </div>
                  <div>
                    +44 <span className={css.blur}>1234 567 891</span>
                  </div>
                </a>
              </li>
            )}
            contents={
              <span className={css.tooltip}>Looking to dig deeper? Sign up to get started</span>
            }
          />
        ))}
      </ul>
      {invisibleContactCount > 0 && (
        <button onClick={() => setLimit((limit) => limit + 3)} className={css.moreContactsBtn}>
          View more <img src={DOWN_CHEVRON_ICON} />
        </button>
      )}
    </div>
  );
}

const CONTACTS = "contacts";
const SUPPLIERS = "suppliers";
const TAB_SELECTOR = "records";

type BuyerLinkSpamProps = {
  buyer_linkspam: BuyerLinkSpamType;
  buyer_name: string;
  buyer_name_formatted: React.ReactNode;
};
/**
 * Linkspam section for buyer landing page
 * @param buyer_linkspam
 * @returns
 */
function BuyerLinkSpam({
  buyer_linkspam,
  buyer_name,
  buyer_name_formatted,
}: BuyerLinkSpamProps): JSX.Element {
  const { similar_buyers, other_buyers, topics_of_interest_to_buyer, buyer_sources } =
    buyer_linkspam;
  return (
    <div className={css.buyerLinkspam}>
      <h2>Find more related buyers</h2>
      <p>
        Discover buyers with similar procurement activity to {buyer_name}. Explore more buyers
        procuring for contracts based similar CPV codes and geographical proximity to {buyer_name}.
      </p>
      {similar_buyers.length > 0 && (
        <NewLinksSection
          title={<h4 className={css.linkSpamSubtitle}>Buyers with similar tenders</h4>}
          links={similar_buyers}
        />
      )}

      <NewLinksSection
        title={<h4 className={css.linkSpamSubtitle}>More potential buyers</h4>}
        links={other_buyers}
        footer={
          <div className={css.exploreMoreContainer}>
            <a
              className={css.exploreMoreText}
              href="/explore/search/buyers"
              data-id="explore_articles"
            >
              Explore over 15,000 buyers
            </a>
          </div>
        }
      />

      {topics_of_interest_to_buyer.length > 0 && (
        <NewLinksSection
          title={
            <h2 className={css.linkSpamTitle}>
              Explore topics of interest to {buyer_name_formatted}
            </h2>
          }
          description={
            <p className={css.linkSpamDescription}>
              Explore similar open tenders, recent contract awards and upcoming contract expiries,
              based on topics relating to {buyer_name}
            </p>
          }
          links={topics_of_interest_to_buyer}
          footer={
            <div className={css.exploreMoreContainer}>
              <a
                className={css.exploreMoreText}
                href="/explore/search/topics"
                data-id="explore_articles"
              >
                Explore more topics
              </a>
            </div>
          }
        />
      )}
      {buyer_sources.length > 0 && (
        <NewLinksSection
          title={
            <h2 className={css.linkSpamTitle}>
              Explore sources {buyer_name_formatted} publishes to
            </h2>
          }
          description={
            <p className={css.linkSpamDescription}>
              Stotles aggregates data from every government procurement data source into one
              platform. Explore the sources {buyer_name} and thousands more publish contracts to
              below.
            </p>
          }
          links={buyer_sources}
          footer={
            <div className={css.lessBottomPaddingContainer}>
              <a
                className={css.exploreMoreText}
                href="/explore/search/sources"
                data-id="explore_articles"
              >
                Explore more sources
              </a>
            </div>
          }
        />
      )}
    </div>
  );
}

type BuyerHeaderProps = {
  enhancedData: EnhancedBuyerData;
};
/**
 * Gets data pertaining to the buyer from oscar API and displays it in a box in the buyer header
 * @param enhancedData
 * @returns
 */
function BuyerHeaderData({ enhancedData }: BuyerHeaderProps): JSX.Element {
  const { annual_spend, employee_count, location_summary, url, oscar_category } = enhancedData;

  const formattedUrl = React.useMemo(() => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `https://${url}`;
  }, [url]);

  /**
   * Formatted version of employee count data
   */
  const employeeCount = React.useMemo(() => {
    switch (employee_count) {
      case null:
      case "Unknown":
        return null;
      default:
        return employee_count?.replace(/ to /, "-");
    }
  }, [employee_count]);

  /**
   * Formatted version of annual spend data
   */
  const annualSpend = React.useMemo(() => {
    if (!annual_spend) return null;

    const currency = "£"; // for now we only have this data for uk buyers
    // these are the categories we receive from oscar. Obviously this is less flexible compared with
    // parsing the vlues programatically, but this is pretty easy to maintain and add to as well
    const valueMapping: Record<string, string> = {
      "20 M - 100 M": `${currency}20-100M`,
      "Over 1 Billion": `Over ${currency}1B`,
      "5 M - 20 M": `${currency}5-20M`,
      "100 M - 500 M": `${currency}100-500M`,
      "0 - 1 M": `0-${currency}1M`,
      "500 M - 1 Billion": `${currency}500M - 1B`,
      "1 M - 5 M": `${currency}1-5M`,
    };

    const formattedValue = valueMapping[annual_spend];

    if (formattedValue) {
      return formattedValue;
    } else {
      return annual_spend;
    }
  }, [annual_spend]);

  return (
    <div className={css.enhancedBuyerData}>
      {annualSpend && (
        <div>
          <p className={css.heading}>Annual spend</p>
          <p className={css.value}>{annualSpend}</p>
        </div>
      )}
      {employeeCount && (
        <div>
          <p className={css.heading}>No. employees</p>
          <p className={css.value}>{employeeCount}</p>
        </div>
      )}
      {location_summary && (
        <div>
          <p className={css.heading}>Location</p>
          <p className={css.value}>{location_summary}</p>
        </div>
      )}
      {oscar_category && (
        <div>
          <p className={css.heading}>Buyer type</p>
          <p className={css.value}>
            {oscar_category.map((oc) => (
              <span key={oc} className={css.oscarCat}>
                {oc}
              </span>
            ))}
          </p>
        </div>
      )}
      {formattedUrl && (
        <a href={formattedUrl} className={css.link} target="_blank" rel="noreferrer">
          Go to buyers website <ExternalLink fill={blue500} className={css.link} />
        </a>
      )}
    </div>
  );
}

function LandingPage({
  config,
  app_root_url,
  buyer_linkspam,
  current_page,
  total_results_count,
  extra_params_for_paging,
  enhanced_buyer_data,
  buyer_guid,
}: Props): JSX.Element {
  const { records, metadata, name, top_suppliers, contacts } = config;

  const visibleSections = React.useMemo(() => {
    return {
      suppliers: top_suppliers.length > 0,
      contacts: contacts.length > 0,
    };
  }, [top_suppliers, contacts]);

  const activeRecordType = config.object_type;

  const breadcrumbs = React.useMemo(() => {
    return [
      {
        id: "home",
        name: "Home",
        href: "/",
      },
      {
        id: "tenders",
        name: "Tenders",
        href: SEO_URLS.tenderSearch,
      },
      {
        id: "buyers",
        name: "Buyers",
        href: `/explore/search/buyers`,
      },
      {
        id: name,
        name: capitalizeFirstLetter(name),
      },
    ];
  }, [name]);

  // Send page view event
  usePageViewLogger();

  const otherPagesTabs = React.useMemo(() => {
    const tabs = [
      ["Open opportunities", "tenders"],
      ["Recent awards", "recent_awards"],
      ["Upcoming contract expiries", "upcoming_expiries"],
    ];

    return (
      <ul className={css.otherPages} id={TAB_SELECTOR} key={activeRecordType}>
        {tabs.map(([label, path]) => {
          const active = activeRecordType === path;
          return (
            <li key={label} className={active ? css.active : undefined}>
              {active ? (
                <div className={classnames(css.tab, css.disabled)}>{label}</div>
              ) : (
                <a href={`?type=${path}`} className={classnames(css.tab, css.inactiveTab)}>
                  {label}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    );
  }, [activeRecordType]);

  const contactsByGroup = React.useMemo(() => {
    const group: Record<Seniority, BuyerLandingPageConfig["contacts"]> = {
      "Chief Officer": [],
      "C-level & Execs": [],
      Management: [],
    };
    for (const contact of contacts) {
      const seniority = oscarToSeniorityMapping(contact.seniority);
      if (!group[seniority]) group[seniority] = [];

      (group[seniority] as BuyerLandingPageConfig["contacts"]).push(contact);
    }
    return group;
  }, [contacts]);

  const buyerName = <em className={css.buyerName}>{name}</em>;

  const promoBuyerName = <em className={css.promoBuyerName}>{name}</em>;

  return (
    <div className={css.landingPage}>
      <Header appRootUrl={app_root_url} dataType="buyer" id={buyer_guid} />
      <main>
        <Breadcrumbs items={breadcrumbs} />
        <section className={css.headerSection}>
          <div>
            <span className={css.headlineWrapper}>
              {metadata?.logo_url && (
                <img src={metadata.logo_url} alt={`${name} logo`} height="48" />
              )}
              <h1 className={css.h1}>
                <span className={css.h1BuyerName}>{buyerName}</span> tenders and contracts
              </h1>
            </span>
            <p className={css.body}>
              Learn everything you need to know about{" "}
              <span className={css.bodyBuyerName}>{name}</span>. Gain insights into{" "}
              <span className={css.bodyBuyerName}>{name}'s</span> purchasing trends, open tenders,
              recent contract awards and upcoming contract expiries. Approach decision-makers within{" "}
              <span className={css.bodyBuyerName}>{name}</span> directly or access{" "}
              <span className={css.bodyBuyerName}>{name}</span> via partner suppliers.
            </p>

            <PromoSection
              text={<>Explore tenders and contracts for {promoBuyerName} and more with Stotles.</>}
              appRootUrl={app_root_url}
              dataType="buyer"
              id={buyer_guid}
            />
          </div>
          {enhanced_buyer_data ? (
            <BuyerHeaderData enhancedData={enhanced_buyer_data} />
          ) : (
            <div className={css.transparentSquare} />
          )}
        </section>

        {otherPagesTabs && (
          <section className={css.section}>
            <h2>{buyerName} tenders & contracts</h2>
            {otherPagesTabs}
            <div id="records">
              <ExpandableRecordList
                records={records}
                hideBuyer
                currentPage={current_page}
                totalResultsCount={total_results_count}
                extraParamsForPaging={extra_params_for_paging}
                promoSection={
                  <PromoSection
                    text={<>Get {promoBuyerName} tenders straight to your inbox</>}
                    appRootUrl={app_root_url}
                    dataType="buyer"
                    id={buyer_guid}
                    large={true}
                    className={css.recordPromoSection}
                  />
                }
              />
            </div>
          </section>
        )}
        {visibleSections.contacts && (
          <section id={CONTACTS} className={css.contactsSection}>
            <h3 className={css.contactsTitle}>Decision makers at {buyerName}</h3>
            <p>
              Go beyond procurement contacts and speak with key decision makers inside {name}{" "}
              directly.
            </p>
            <div>
              {Object.entries(contactsByGroup).map(([label, cntcts]) => (
                <ContactsRow
                  contacts={cntcts as BuyerLandingPageConfig["contacts"]}
                  label={label}
                  key={label}
                  appRootUrl={app_root_url}
                  buyer_guid={buyer_guid}
                />
              ))}
            </div>
          </section>
        )}
        {visibleSections.suppliers && (
          <section id={SUPPLIERS} className={css.supplierSection}>
            <h2>Top suppliers to {buyerName}</h2>
            <p>
              Explore relationships between {name} and suppliers to understand where partner
              opportunities exist, and competitor incumbents prevail.
            </p>
            <table className={css.supplierTable}>
              <thead>
                <tr>
                  <th>Supplier name</th>
                  <th>SME?</th>
                  <th>Number of awards</th>
                  <th>View awards</th>
                </tr>
              </thead>
              <tbody>
                {top_suppliers.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <HoverTooltip
                        renderTrigger={(showTooltip, hideTooltip, setPosition) => (
                          <a
                            href={signupLink(app_root_url, "buyer", buyer_guid)}
                            onMouseOver={showTooltip}
                            onMouseOut={hideTooltip}
                            onMouseMove={setPosition}
                            className={css.supplierTableLink}
                            data-id="signup_cta"
                          >
                            {s.name}
                          </a>
                        )}
                        contents={
                          <span className={css.tooltip}>
                            Looking to dig deeper? Sign up to get started
                          </span>
                        }
                      />
                    </td>
                    <td>{s.sme ? "Yes" : s.sme === false ? "No" : "Unknown"}</td>
                    <td>{s.award_count}</td>
                    <td>
                      <HoverTooltip
                        renderTrigger={(showTooltip, hideTooltip, setPosition) => (
                          <a
                            href={signupLink(app_root_url, "buyer", buyer_guid)}
                            onMouseOver={showTooltip}
                            onMouseOut={hideTooltip}
                            onMouseMove={setPosition}
                            className={css.supplierTableLink}
                            data-id="signup_cta"
                          >
                            View awards
                          </a>
                        )}
                        contents={
                          <span className={css.tooltip}>
                            Looking to dig deeper? Sign up to get started
                          </span>
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        <FooterSignUpSection appRootUrl={app_root_url} dataType="buyer" id={buyer_guid} />
        <BuyerLinkSpam
          buyer_linkspam={buyer_linkspam}
          buyer_name={name}
          buyer_name_formatted={buyerName}
        />
      </main>
      <Footer />
    </div>
  );
}

export default LandingPage;
